import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { systemSettingsService, DEFAULT_SETTINGS, type SystemSettings } from '@/services/systemSettingsService';

interface SystemSettingsContextValue {
  settings: SystemSettings;
  hasLogo: boolean;
  logoVersion: number;  // tăng lên khi upload logo mới → force reload img
  refresh: () => Promise<void>;
  bumpLogoVersion: () => void;
}

const Ctx = createContext<SystemSettingsContextValue>({
  settings: DEFAULT_SETTINGS,
  hasLogo: false,
  logoVersion: 0,
  refresh: async () => {},
  bumpLogoVersion: () => {},
});

export function SystemSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<SystemSettings>(DEFAULT_SETTINGS);
  const [hasLogo, setHasLogo] = useState(false);
  const [logoVersion, setLogoVersion] = useState(0);

  const refresh = async () => {
    try {
      const s = await systemSettingsService.get();
      setSettings({ ...DEFAULT_SETTINGS, ...s });

      // Backend mới trả flag has_logo trong settings -> dùng luôn, không cần HEAD request riêng
      // (HEAD bị nginx hoặc Kestrel reject 405 trên 1 số setup -> bug logo không hiện)
      if (s.has_logo === '1') {
        setHasLogo(true);
        return;
      }
      if (s.has_logo === '0') {
        setHasLogo(false);
        return;
      }
    } catch { /* giữ default */ }

    // Fallback cho backend cũ chưa trả has_logo: thử GET (không HEAD)
    try {
      const r = await fetch('/api/system/logo', { method: 'GET', cache: 'no-store' });
      setHasLogo(r.ok);
    } catch { setHasLogo(false); }
  };

  useEffect(() => {
    refresh();
  }, []);

  // Update tab title khi system_name đổi
  useEffect(() => {
    const title = settings.system_name || 'HV-TaskFlow';
    document.title = title;
  }, [settings.system_name]);

  // Apply font + base font-size lên html/body
  useEffect(() => {
    const fontFamily = settings.font_family || 'Inter, system-ui, -apple-system, sans-serif';
    const fontSize = settings.font_size_base || '14';
    document.documentElement.style.setProperty('--app-font-family', fontFamily);
    document.documentElement.style.setProperty('--app-font-size', fontSize + 'px');
    document.body.style.fontFamily = fontFamily;
    document.body.style.fontSize = fontSize + 'px';
  }, [settings.font_family, settings.font_size_base]);

  return (
    <Ctx.Provider value={{
      settings,
      hasLogo,
      logoVersion,
      refresh,
      bumpLogoVersion: () => setLogoVersion(v => v + 1),
    }}>
      {children}
    </Ctx.Provider>
  );
}

export function useSystemSettings() {
  return useContext(Ctx);
}
